import GoldenLayout from 'golden-layout';
import {
    AddLayoutDragSourcePubSub,
    AddLayoutDragSourcePubSubItem,
} from '../appToolbar/AddLayoutDragSourcePubSub';
import { LayoutRendererReactComponent } from './LayoutRendererComponent';
import { AnyConfig } from '../widgets/WidgetRegistry';
import { WidgetId } from '../widgets/WidgetId';
import { Store } from 'redux';
import Services from '../store/Services';
import { v4 as uuid } from 'uuid';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';

export interface DraggedLayoutRendererReactComponent
    extends LayoutRendererReactComponent {
    draggedItem: {
        config?: unknown;
    };
}

export interface DraggedContentItem extends GoldenLayout.ContentItem {
    config: DraggedLayoutRendererReactComponent;
}

interface GoldenLayoutWithDragSourceTyped extends GoldenLayout {
    createDragSource: (
        element: HTMLElement,
        itemConfiguration: GoldenLayout.ItemConfigType
    ) => {
        _dragListener: {
            on: (eventName: string, cb: () => void) => void;
        };
    };
}

type AddedComponent = {
    addingComponent: DraggedLayoutRendererReactComponent;
    item: AddLayoutDragSourcePubSubItem;
};
export class LayoutRendererDragDropManager {
    private gl: GoldenLayoutWithDragSourceTyped;
    private addLayoutDragSourcePubSub: AddLayoutDragSourcePubSub;

    private readonly onDraggingWidget: (added: {
        id: WidgetId;
        config: AnyConfig | undefined;
    }) => void;
    private readonly onDraggedWidgetComplete: (added: { id: WidgetId }) => void;
    private readonly store: Store;
    private readonly services: Services;
    private logger: Logger;

    private lastDragged: WidgetId | undefined;

    private addedComponents: Array<AddedComponent> = [];

    public constructor(options: {
        gl: GoldenLayout;
        addLayoutDragSourcePubSub: AddLayoutDragSourcePubSub;
        onDraggingWidget: (added: { id: WidgetId; config?: AnyConfig }) => void;
        onDraggedWidgetComplete: (added: { id: WidgetId }) => void;
        store: Store;
        services: Services;
    }) {
        this.gl = options.gl as GoldenLayoutWithDragSourceTyped;
        this.addLayoutDragSourcePubSub = options.addLayoutDragSourcePubSub;
        this.onDraggingWidget = options.onDraggingWidget;
        this.onDraggedWidgetComplete = options.onDraggedWidgetComplete;
        this.store = options.store;
        this.services = options.services;

        this.addLayoutDragSourcePubSub.subscribeAdd(
            'LayoutRendererDragDropManager_' + uuid(),
            this.onAddDragSourceItem.bind(this)
        );

        this.logger = buildLogger(
            'LayoutRendererDragDropManager',
            this.services.loggingService
        );
    }

    public onItemCreated(item: GoldenLayout.ContentItem) {
        const draggedItem = item as DraggedContentItem;

        if (!draggedItem.config.draggedItem) {
            return;
        }

        const widgetType = draggedItem.config.props.componentType;

        // Ensure we create unique widget keys...
        const widgetKey =
            this.services.widgetsService.getNextWidgetId(widgetType);
        draggedItem.config.props.componentId = widgetKey;

        const config = draggedItem.config.draggedItem.config;

        // Remember the dragged widget, so we can clean up in onDragComplete...
        this.lastDragged = { widgetType, widgetKey };

        this.onDraggingWidget({
            id: { widgetKey, widgetType },
            config,
        });
    }

    public updateGl(gl: GoldenLayout) {
        this.gl = gl as GoldenLayoutWithDragSourceTyped;

        const addedUids: string[] = [];

        for (let i = this.addedComponents.length - 1; i > -1; i--) {
            const added = this.addedComponents[i];
            const uid = added.item.uid;
            if (addedUids.indexOf(uid) === -1) {
                addedUids.push(uid);
                this.subscribeDragStart(added.addingComponent, added.item);
            }
        }
    }

    /*
     * Creates a drag source for the given element
     */
    private onAddDragSourceItem(item?: AddLayoutDragSourcePubSubItem) {
        if (!item) {
            return;
        }

        const { type, title, config } = item;
        const componentId = this.services.widgetsService.getNextWidgetId(type);

        const addingComponent: DraggedLayoutRendererReactComponent = {
            component: type,
            type: 'react-component',
            title,
            isClosable: true,
            props: {
                componentType: type,
                store: this.store,
                services: this.services,
                componentId,
            },
            draggedItem: {
                config,
            },
        };

        this.addedComponents.push({ addingComponent, item });

        this.subscribeDragStart(addingComponent, item);
    }

    /*
     * Called when the render state has changed and we are not dragging....
     */
    public onDragComplete() {
        if (!this.lastDragged) {
            return;
        }

        this.onDraggedWidgetComplete({
            id: this.lastDragged,
        });
        this.addLayoutDragSourcePubSub.publishDragged(this.lastDragged);

        this.lastDragged = undefined;
    }

    private subscribeDragStart(
        addingComponent: DraggedLayoutRendererReactComponent,
        item: AddLayoutDragSourcePubSubItem
    ) {
        const { element } = item;
        const dragSource = this.gl!.createDragSource(element, addingComponent);
        const dragListener = dragSource._dragListener;

        dragListener.on('dragStart', () => {
            this.addLayoutDragSourcePubSub.publishDragging(item);
        });
    }
}
